// _sys/blocks/primitives/EnvelopeInspector.tsx
"use client";

import React, { useState } from "react";
import styled from "styled-components";
import type { EnvelopeInspectorBlock } from "../../_sys/_types";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { getIcon } from "../blocks/shared";

const Wrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.large} 0;
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.static.black};
  border-radius: ${({ theme }) => theme.borders.radius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.disabled};
`;

const PanelTitle = styled.span`
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  color: ${({ theme }) => theme.colors.static.white};
`;

const LanguageTag = styled.span`
  font-family: ${({ theme }) => theme.typography.fonts.medium};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  color: ${({ theme }) => theme.colors.static.grey};
  text-transform: uppercase;
  margin-left: ${({ theme }) => theme.spacing.small};
`;

const CopyButton = styled.button<{ $copied: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  background: transparent;
  color: ${({ theme, $copied }) =>
    $copied ? theme.colors.brand.green : theme.colors.static.grey};
  font-family: ${({ theme }) => theme.typography.fonts.medium};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borders.radius.small};
  transition: color ${({ theme }) => theme.animations.duration.short}
    ${({ theme }) => theme.animations.easing.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.static.white};
  }
`;

const CodeBody = styled.pre`
  margin: 0;
  padding: ${({ theme }) => theme.spacing.medium};
  overflow-x: auto;
  font-family: "RobotoFlex-VariableFont", monospace;
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  line-height: ${({ theme }) => theme.typography.lineHeights.body};
  color: ${({ theme }) => theme.colors.static.white};

  .tok-key {
    color: #7fd6ff;
  }
  .tok-string {
    color: #ffb37f;
  }
  .tok-number {
    color: #baff7f;
  }
  .tok-boolean {
    color: #d09bff;
  }
  .tok-punct {
    color: ${({ theme }) => theme.colors.static.grey};
  }
`;

/** Minimal JSON tokenizer — just enough coloring to read the shape at a glance. */
function highlightJson(source: string): string {
  const escaped = source
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      if (/^"/.test(match)) {
        return /:$/.test(match)
          ? `<span class="tok-key">${match.replace(/:$/, "")}</span>:`
          : `<span class="tok-string">${match}</span>`;
      }
      if (/true|false/.test(match))
        return `<span class="tok-boolean">${match}</span>`;
      if (match === "null") return `<span class="tok-boolean">${match}</span>`;
      return `<span class="tok-number">${match}</span>`;
    },
  );
}

export const EnvelopeInspector: React.FC<{ block: EnvelopeInspectorBlock }> = ({
  block,
}) => {
  const containerRef = useScrollReveal({ selector: "self", y: 20 });
  const [copied, setCopied] = useState(false);
  const CopyIcon = getIcon("copy");
  const CheckIcon = getIcon("checkCircle");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(block.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API unavailable — silently ignore, button just won't confirm.
    }
  };

  const html =
    block.language === "json" ? highlightJson(block.content) : block.content;

  return (
    <Wrapper ref={containerRef}>
      <Panel>
        <PanelHeader>
          <span>
            <PanelTitle>{block.title || "Example"}</PanelTitle>
            <LanguageTag>{block.language}</LanguageTag>
          </span>
          <CopyButton type="button" onClick={handleCopy} $copied={copied}>
            {copied ? (
              <CheckIcon size={14} weight="bold" />
            ) : (
              <CopyIcon size={14} weight="bold" />
            )}
            {copied ? "Copied" : "Copy"}
          </CopyButton>
        </PanelHeader>
        {block.language === "json" ? (
          <CodeBody dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <CodeBody>{block.content}</CodeBody>
        )}
      </Panel>
    </Wrapper>
  );
};
